import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { API_BASE } from "../utils/api";
import { hasAccountAccess } from "../utils/permissions";
import { isBMI, BMI_CUSTOMER_IDS } from "../utils/config";
import Unauthorized from "./Unauthorized";
import { useLanguage } from "../context/LanguageContext";
import CustomDatePicker from "../components/UI/CustomDatePicker";

export default function OrderHistory() {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    const isSupervisor = currentUser.groupId === "     9";
    const { t, switchLanguage, language } = useLanguage();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const cachedCount = localStorage.getItem("pending_approval_count");
    const [pendingCount, setPendingCount] = useState(cachedCount !== null ? Number(cachedCount) : null);
    const history = useHistory();

    const location = useLocation();

    const [reference, setReference] = useState("");
    const [view, setView] = useState("ALL");
    const [customerShipTos, setCustomerShipTos] = useState([]);

    // ✅ Agregados los estados de fecha que requiere el DatePicker
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const [from, setFrom] = useState(firstDayOfMonth);
    const [to, setTo] = useState(today);

    const TAB_ORDERS = "orders";
    const TAB_INVOICES = "invoices";
    const TAB_GUIAS = "guias";
    const TAB_PROFORMAS = "proformas";
    const TAB_PENDING_APPROVAL = "pending_approval";

    const initialTab = location.state?.activeTab ?? TAB_ORDERS;
    const [activeTab, setActiveTab] = useState(initialTab);


    // Arma los params para un id_customer puntual — se usa una vez por cada
    // id_customer a consultar (normalmente solo el propio, salvo "Pendientes de
    // Aprobación" para un Aprobador BMI, que consulta ambas cuentas UIO+GYE).
    // includeDates=false para el conteo del badge (loadPendingCount): ese contador
    // siempre debe ser el total real de pendientes, sin importar el rango de
    // fechas que el usuario tenga seleccionado en los datepickers de la lista.
    const buildOrderParams = (tab, idCustomer, { includeDates = true } = {}) => {
        const params = new URLSearchParams();
        if (includeDates) {
            params.append("date_from", from.toISOString().split("T")[0]);
            params.append("date_to", to.toISOString().split("T")[0]);
        }

        params.append("id_customer", idCustomer);
        // El Aprobador siempre ve TODAS las sucursales en todo orders-history
        // (ninguna pestaña, no se manda id_ship_to, el backend lo recibe NULL),
        // sin importar en que sucursal haya quedado parada su sesion — no tiene
        // opcion de cambiar de sucursal, se le muestra todo de una. Los demas
        // roles (Asesor, Cliente) siempre mandan un numero concreto: 0 = MATRIZ.
        if (!isSupervisor) {
            params.append("id_ship_to", Number(currentUser.idShipTo) || 0);
        }

        // 🔁 DIFERENCIA ENTRE TABS
        if (tab === TAB_ORDERS) {
            params.append("clase", "*");
            params.append("tipo", "PE");
            params.append("tipo2", "null");
        }

        if (tab === TAB_PROFORMAS) {
            params.append("clase", " ");
            params.append("tipo", "PE");
            params.append("tipo2", "null");
        }

        if (tab === TAB_GUIAS) {
            params.append("clase", " ");
            params.append("tipo", "FE");
            params.append("tipo2", "FJ");
        }

        if (tab === TAB_INVOICES) {
            params.append("clase", " ");
            params.append("tipo", "FC");
            params.append("tipo2", "null");
        }

        if (tab === TAB_PENDING_APPROVAL) {
            params.append("myaccount_id", currentUser.myaccountId);
            params.append("clase", "+");
            params.append("tipo", "PE");
        }

        // opcionales — el filtro de referencia solo aplica a la lista visible,
        // nunca al conteo del badge (igual que las fechas).
        if (includeDates) {
            let isReference = false;
            if (
                reference !== null &&
                reference !== "" &&
                !Number.isNaN(Number(reference))
            ) {
                params.append("order_number", reference);
                isReference = true;
            }

            if (!isReference) {
                if (from) params.append("date_from", from);
                if (to) params.append("date_to", to);
            }
        }

        return params;
    };

    // El Aprobador de una marca BMI ve TODO el historial (cualquier pestaña:
    // ordenes, guias, facturas, pendientes) sumando las dos cuentas (UIO+GYE) —
    // son la misma marca para efectos de aprobacion, aunque en el ERP sean dos
    // id_customer distintos (igual que en Mantenimiento de Presupuestos). Los
    // demas roles (Asesor, Cliente) se quedan con el id_customer de su sesion.
    const idCustomersFor = () =>
        isSupervisor && isBMI(currentUser) ? BMI_CUSTOMER_IDS : [currentUser.idCustomer];

    const fetchOrdersAcrossCustomers = async (tab, options) => {
        const results = await Promise.all(
            idCustomersFor().map((idCustomer) =>
                fetch(`${API_BASE}/orders-ultra/api/ws/list_orders_ecommerce?${buildOrderParams(tab, idCustomer, options).toString()}`)
                    .then((r) => r.json())
                    .then((d) => (Array.isArray(d) ? d : []))
            )
        );
        return results.flat();
    };

    const loadOrders = async (tab = TAB_ORDERS) => {
        setLoading(true);
        try {
            const data = await fetchOrdersAcrossCustomers(tab);
            setOrders(data);
            if (tab === TAB_PENDING_APPROVAL) {
                setPendingCount(data.length);
                localStorage.setItem("pending_approval_count", data.length);
            }
        } catch (err) {
            console.error("Error cargando datos", err);
        } finally {
            setLoading(false);
        }
    };

    const loadPendingCount = async () => {
        try {
            const data = await fetchOrdersAcrossCustomers(TAB_PENDING_APPROVAL, { includeDates: false });
            setPendingCount(data.length);
            localStorage.setItem("pending_approval_count", data.length);
        } catch (err) {
            console.error("Error cargando pendientes count", err);
        }
    };

    useEffect(() => {
        loadOrders(initialTab);
        loadPendingCount();

        // La columna Sucursal solo tiene sentido si el cliente tiene mas de una.
        if (isSupervisor) {
            fetch(
                `${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${currentUser.idEmployee}&myaccount_id=${currentUser.myaccountId}&has_ship_to=true&id_customer=${currentUser.idCustomer}`
            )
                .then((r) => r.json())
                .then((data) => setCustomerShipTos(Array.isArray(data) ? data.filter((s) => s.idShipTo) : []))
                .catch((err) => console.error("Error cargando sucursales", err));
        }
    }, []);

    const hasAccess = hasAccountAccess();
    if (!hasAccess) {
        return <Unauthorized />;
    }
    const showUserCol = activeTab !== TAB_INVOICES && activeTab !== TAB_GUIAS && activeTab !== TAB_PROFORMAS;
    const showBranchCol = isSupervisor && showUserCol && customerShipTos.length > 0;
    const showStatusCol = showUserCol && activeTab !== TAB_PENDING_APPROVAL;

    // Tailwind necesita ver la clase completa como texto literal para generarla —
    // no se puede armar el valor arbitrario de grid-cols dinamicamente en runtime.
    let gridTemplate;
    if (!showUserCol) {
        gridTemplate = "md:grid-cols-[1fr_1fr_2fr_1fr]"; // facturas / guias / proformas
    } else if (showBranchCol && showStatusCol) {
        gridTemplate = "md:grid-cols-[1fr_1fr_1fr_1fr_2fr_1fr_1fr]"; // ordenes, aprobador
    } else if (showBranchCol && !showStatusCol) {
        gridTemplate = "md:grid-cols-[1fr_1fr_1fr_1fr_2fr_1fr]"; // pendientes, aprobador
    } else if (!showBranchCol && showStatusCol) {
        gridTemplate = "md:grid-cols-[1fr_1fr_1fr_2fr_1fr_1fr]"; // ordenes, usuario normal
    } else {
        gridTemplate = "md:grid-cols-[1fr_1fr_1fr_2fr_1fr]"; // pendientes, usuario normal
    }

    return (
        <div className="w-full px-6 py-8 grid grid-cols-12 gap-6 pt-[130px]">

            {/* SIDEBAR */}
            <aside className="col-span-12 md:col-span-3">
                <div className="bg-white border rounded-xl p-4">
                    <h2 className="font-bold text-lg mb-4">{t.ordersHistoryTitle}</h2>
                    <button
                        onClick={() => {
                            setActiveTab(TAB_PROFORMAS);
                            loadOrders(TAB_PROFORMAS);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition hidden
                            ${activeTab === TAB_PROFORMAS
                                ? "bg-brand-primary text-white"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                    >
                        Mis Solicitudes de Proforma
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab(TAB_ORDERS);
                            loadOrders(TAB_ORDERS);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg mb-2 transition
                            ${activeTab === TAB_ORDERS
                                ? "bg-brand-primary text-white"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                    >
                        {t.opBtnMyOrders}
                    </button>

                    <button
                        onClick={() => {
                            setActiveTab(TAB_PENDING_APPROVAL);
                            loadOrders(TAB_PENDING_APPROVAL);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg mb-2 transition font-semibold
                            flex items-center justify-between gap-2
                            ${activeTab === TAB_PENDING_APPROVAL
                                ? "bg-green-500 text-white"
                                : "bg-green-100 text-green-800 hover:bg-green-200"
                            }`}
                    >
                        <span>{t.opBtnPendingAprove}</span>
                        {pendingCount === null ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full">
                                <span className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                            </span>
                        ) : (
                            <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5
                                rounded-full text-xs font-bold leading-none shadow-sm
                                ${activeTab === TAB_PENDING_APPROVAL
                                    ? "bg-white text-brand-primary"
                                    : "bg-brand-primary text-white"
                                }`}>
                                {pendingCount > 99 ? "99+" : pendingCount}
                            </span>
                        )}
                    </button>


                    <button
                        onClick={() => {
                            setActiveTab(TAB_GUIAS);
                            loadOrders(TAB_GUIAS);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition
                            ${activeTab === TAB_GUIAS
                                ? "bg-brand-primary text-white"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                    >
                        {t.opBtnGuies}
                    </button>

                    <button
                        onClick={() => {
                            setActiveTab(TAB_INVOICES);
                            loadOrders(TAB_INVOICES);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition
                            ${activeTab === TAB_INVOICES
                                ? "bg-brand-primary text-white"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                    >
                        {t.opBtnInvoices}
                    </button>
                </div>
            </aside>

            {/* MAIN */}
            <section className="col-span-12 md:col-span-9">
                <div className="bg-white border rounded-xl p-6">

                    <h3 className="font-semibold text-lg mb-4">Buscar por</h3>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 relative z-30">
                        {/* ✅ TUS DATEPICKERS CON LA NUEVA LÓGICA GLOBAL */}
                        <CustomDatePicker selected={from} onChange={(d) => setFrom(d)} />
                        <CustomDatePicker selected={to} onChange={(d) => setTo(d)} />


                        <input
                            placeholder="Reference Number"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    loadOrders(activeTab);
                                }
                            }}
                            className="border border-gray-300 rounded-lg px-3 py-2
                            focus:outline-none focus:border-brand-primary focus:ring-0"
                        />

                        {activeTab !== TAB_INVOICES && (
                            <select
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                                className="border rounded-lg px-3 py-2
                                 focus:outline-none focus:border-brand-primary focus:ring-0"
                            >
                                <option value="ALL">All Orders</option>
                                <option value="PENDING">Pendientes</option>
                                <option value="SHIPPED">Facturadas</option>
                            </select>
                        )}

                    </div>

                    <button
                        onClick={() => loadOrders(activeTab)}
                        className="bg-brand-primary text-white px-6 py-2 rounded-lg
                        hover:opacity-90 mb-6 flex items-center gap-2"
                    >
                        <FaSearch className="text-sm" />
                        <span>{t.btnSearch}</span>
                    </button>

                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">{orders.length} Results</span>
                        <span className="whitespace-nowrap">
                            <span className="text-l text-gray-500">Total: </span>
                            <span className="text-l font-bold text-brand-primary tabular-nums">
                                ${orders.reduce((sum, o) => sum + Number(o.total || 0), 0).toFixed(2)}
                            </span>
                            <span className="text-l text-gray-500">
                               &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                   &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                     &nbsp;&nbsp;
                            </span>

                        </span>
                    </div>

                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">

                        <div
                            className={`hidden md:grid ${gridTemplate} px-4 py-3 bg-gray-100
                            border-b border-gray-300 text-gray-700 font-semibold text-sm
                            sticky top-0 z-20`}
                        >
                            {showBranchCol && <span>Sucursal</span>}
                            {showUserCol && <span>Usuario</span>}
                            <span>Fecha</span>
                            <span className="text-right">
                                {activeTab === TAB_ORDERS ? "Orden Nro" : "Número"}
                            </span>
                            <span className="text-center">Orden de Compra</span>
                            <span className="text-right">Total</span>
                            {showStatusCol && <span className="text-center">Estado</span>}

                        </div>

                        {loading && (
                            <p className="text-gray-500 text-center py-4">
                                Cargando ordenes...
                            </p>
                        )}

                        {!loading && orders.length === 0 && (
                            <p className="text-gray-500">
                                No tienes ordenes registrados...
                            </p>
                        )}

                        {!loading &&
                            orders.map((o) => (
                                <div
                                    key={o.order_id}
                                    onClick={() =>
                                        history.push(`/order-details/${o.orderId}`, { order: o })
                                    }
                                    className={`bg-white border border-gray-200 rounded-lg p-3 shadow-sm
                                    grid grid-cols-1 ${gridTemplate}
                                    gap-2 items-center
                                    hover:bg-gray-200 hover:shadow-lg transition cursor-pointer`}
                                >
                                    {showBranchCol && (
                                        <div className="text-gray-700 text-sm">
                                            {o.shipToName || "MATRIZ"}
                                        </div>
                                    )}
                                    {showUserCol && (
                                        <div className="text-gray-800 font-medium text-sm">
                                            {o.employeeId}
                                        </div>
                                    )}

                                    <div className="text-gray-700 text-sm">
                                        {new Date(o.orderDate).toLocaleDateString("es-ES", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric"
                                        })}
                                    </div>

                                    <div className="text-gray-800 font-semibold text-sm text-right">
                                        {o.localId !== null && o.issueId !== null &&
                                            o.localId.trim() !== "" && o.issueId.trim() !== ""
                                            ? `${o.localId.trim()} - ${o.issueId.trim()} - ${o.orderNumber}`
                                            : o.orderNumber}
                                    </div>

                                    <div className="text-gray-800 font-semibold text-sm text-center">
                                        {o.invoiceNumber ?? "—"}
                                    </div>

                                    <div className="text-ultra-red text-right font-bold text-sm">
                                        ${Number(o.total || 0).toFixed(2)}
                                    </div>

                                    {showStatusCol && (
                                        <span className="inline-block px-2 py-1 text-[11px] font-semibold text-center">
                                            {o.clase === "+" ? "PENDIENTE DE APROBACIÓN" : o.status}
                                        </span>
                                    )}



                                </div>
                            ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
